import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { creativeStrategyApi, type StrategyDocketDetail, type StrategyDocketVersionDetail } from '@/services/api';
import { Chip, Collapsible, ErrorNote, Field, Loading, Pre, Section, Stat, WarningBanner, formatDate, toText } from './shared';

function Flags({ v }: { v: Pick<StrategyDocketVersionDetail, 'is_latest' | 'is_served' | 'is_pinned' | 'is_promoted'> }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {v.is_served && <Chip tone="emerald">served</Chip>}
      {v.is_latest && <Chip tone="navy">latest</Chip>}
      {v.is_pinned && <Chip tone="amber">pinned</Chip>}
      {v.is_promoted && <Chip tone="emerald">promoted</Chip>}
    </span>
  );
}

function num(v: unknown): string | null {
  return typeof v === 'number' ? v.toLocaleString() : null;
}

function ManifestSummary({ manifest }: { manifest: Record<string, unknown> }) {
  const resolve = (manifest.resolve as Record<string, unknown> | undefined) || {};
  const inputCounts = (resolve.input_counts as Record<string, number> | undefined) || {};
  const tplAdv = (manifest.template_advertisers as Record<string, number> | undefined) || {};
  const overlays = (manifest.overlays as Record<string, Record<string, unknown>> | undefined) || {};
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
        {['run_id', 'published_at', 'generated_at', 'publisher'].map((k) =>
          manifest[k] ? (
            <div key={k}>
              <dt className="text-[10px] uppercase tracking-wide text-slate-400">{k.replace('_', ' ')}</dt>
              <dd className="font-mono text-xs break-all">{k.endsWith('_at') ? formatDate(String(manifest[k])) : toText(manifest[k])}</dd>
            </div>
          ) : null,
        )}
        {resolve.method ? (
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-slate-400">resolve</dt>
            <dd className="text-xs">
              {toText(resolve.method)}
              {resolve.threshold != null && ` @ ${toText(resolve.threshold)}`}
              {resolve.model ? <span className="block font-mono text-[10px] text-slate-500">{toText(resolve.model)}</span> : null}
            </dd>
          </div>
        ) : null}
        {Object.keys(tplAdv).length > 0 && (
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-slate-400">advertisers per template</dt>
            <dd className="text-xs tabular-nums">min {tplAdv.min} · median {tplAdv.median} · max {tplAdv.max}</dd>
          </div>
        )}
      </dl>
      {Object.keys(inputCounts).length > 0 && (
        <div className="text-xs text-slate-600">
          Merged inputs → output:{' '}
          {Object.entries(inputCounts).map(([k, n], i) => (
            <span key={k}>
              {i > 0 && ' · '}
              {k} {num(n)}
              {num((manifest.counts as Record<string, number> | undefined)?.[k]) && ` → ${num((manifest.counts as Record<string, number>)[k])}`}
            </span>
          ))}
        </div>
      )}
      {Object.keys(overlays).length > 0 && (
        <table className="text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
              <th className="pr-4 py-1 font-medium">Overlay</th>
              <th className="pr-4 py-1 font-medium">method</th>
              <th className="pr-4 py-1 font-medium">in client graph</th>
              <th className="pr-4 py-1 font-medium">client runs it</th>
              <th className="py-1 font-medium">gaps</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(overlays).map(([uid, o]) => (
              <tr key={uid}>
                <td className="pr-4 py-0.5 font-mono">{uid}</td>
                <td className="pr-4 py-0.5">{toText(o.method)}</td>
                <td className="pr-4 py-0.5 tabular-nums">{toText(o.in_client_graph)}</td>
                <td className="pr-4 py-0.5 tabular-nums">{toText(o.client_runs_it)}</td>
                <td className="py-0.5 tabular-nums">{toText(o.gaps)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function DocketVersionsTab({ docketId, detail }: { docketId: string; detail: StrategyDocketDetail }) {
  const [selected, setSelected] = useState<string | null>(detail.served_version || detail.versions[0]?.version || null);
  const [version, setVersion] = useState<StrategyDocketVersionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    creativeStrategyApi
      .docketVersion(docketId, selected)
      .then((v) => !cancelled && setVersion(v))
      .catch((err: Error) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [docketId, selected]);

  const memberPacks = version?.sources?.member_packs || {};
  const idx = version?.exemplar_index;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-6">
      <div className="text-sm">
        <p className="text-xs text-slate-500 mb-2">
          Last run <span className="font-mono">{detail.last_run_id ?? '—'}</span>
        </p>
        <ul className="space-y-1">
          {detail.versions.map((v) => (
            <li key={v.version}>
              <button
                type="button"
                onClick={() => setSelected(v.version)}
                className={clsx(
                  'w-full text-left rounded-lg px-2.5 py-1.5 border',
                  selected === v.version ? 'border-brand-teal bg-teal-50/40' : 'border-transparent hover:bg-slate-50',
                )}
              >
                <div className="font-mono text-xs text-slate-800">{v.version}</div>
                <div className="flex flex-wrap gap-1 mt-0.5 text-[10px] text-slate-500">
                  {v.version === detail.served_version && <span className="text-emerald-700">served</span>}
                  {v.is_latest && <span>latest</span>}
                  {v.version === detail.pinned_version && <span className="text-amber-700">pinned</span>}
                  {v.version === detail.promoted_version && <span className="text-emerald-700">promoted</span>}
                  {(v.manifest?.published_at || v.manifest?.generated_at) && (
                    <span>{formatDate(v.manifest.published_at || v.manifest.generated_at)}</span>
                  )}
                </div>
              </button>
            </li>
          ))}
          {detail.versions.length === 0 && <li className="text-slate-500">No versions published.</li>}
        </ul>
      </div>

      <div className="min-w-0">
        {loading && !version && <Loading label="Loading version…" />}
        {error && <ErrorNote error={error} />}
        {version && (
          <div className={clsx('space-y-6', loading && 'opacity-60')}>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-mono text-sm text-navy-900">{version.version}</h3>
              <Flags v={version} />
              {version.generated_at && <span className="text-xs text-slate-500">generated {formatDate(version.generated_at)}</span>}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(version.counts).map(([k, n]) => (
                <Stat key={k} label={k.replace('_', ' ')} value={n.toLocaleString()} />
              ))}
              <Stat label="Exemplar ads indexed" value={idx ? (idx.available ? idx.ads.toLocaleString() : 'unavailable') : '—'} />
            </div>

            {version.provenance && <Field label="Provenance">{version.provenance}</Field>}

            {version.caveats.length > 0 && (
              <Field label="Caveats">
                <ul className="list-disc pl-5 space-y-0.5 text-slate-700">
                  {version.caveats.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </Field>
            )}

            <Section title="Built from">
              <div className="space-y-3">
                <Field label={`Client packs (${Object.keys(memberPacks).length})`}>
                  {Object.keys(memberPacks).length === 0 ? (
                    <span className="text-slate-500">none</span>
                  ) : (
                    <ul className="space-y-0.5">
                      {Object.entries(memberPacks).map(([uid, ver]) => (
                        <li key={uid} className="font-mono text-xs">
                          {uid} <span className="text-slate-400">@</span> {ver}
                        </li>
                      ))}
                    </ul>
                  )}
                </Field>
                <Field label="Mining runs">
                  {version.source_runs.length === 0 ? (
                    <span className="text-slate-500">none</span>
                  ) : (
                    <ul className="space-y-0.5">
                      {version.source_runs.map((r) => <li key={r} className="font-mono text-xs break-all">{r}</li>)}
                    </ul>
                  )}
                </Field>
                <Field label="Clients drawing on this library">
                  {version.members.length ? version.members.join(', ') : <span className="text-slate-500">none</span>}
                </Field>
                {Array.isArray(version.scope?.paths) && (version.scope.paths as string[]).length > 0 && (
                  <Field label="Scope paths">
                    <ul className="text-xs space-y-0.5">
                      {(version.scope.paths as string[]).map((p) => <li key={p}>{p}</li>)}
                    </ul>
                  </Field>
                )}
              </div>
            </Section>

            {idx && (
              <Section title="Exemplar index">
                <div className="space-y-2 text-sm">
                  {idx.notes && idx.notes.map((n, i) => <WarningBanner key={i}>{n}</WarningBanner>)}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                    <span className={idx.available ? 'text-emerald-700' : 'text-amber-700'}>{idx.available ? 'available' : 'not available'}</span>
                    <span>{idx.ads.toLocaleString()} ads</span>
                    {idx.generated_at && <span>built {formatDate(idx.generated_at)}</span>}
                    {idx.sources.length > 0 && <span className="break-all">sources: {idx.sources.map(toText).join(', ')}</span>}
                  </div>
                </div>
              </Section>
            )}

            <Section title="Manifest">
              <div className="space-y-3">
                <ManifestSummary manifest={version.manifest} />
                <Collapsible label="Raw manifest JSON">
                  <Pre>{JSON.stringify(version.manifest, null, 2)}</Pre>
                </Collapsible>
                <Collapsible label="Raw sources JSON">
                  <Pre>{JSON.stringify(version.sources, null, 2)}</Pre>
                </Collapsible>
              </div>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
